import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";

const formatDay = (iso) => new Date(iso).getDate();
const formatMonth = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short" });

const Blog = () => (
  <section id="blog" data-testid="blog-section" className="py-24 lg:py-16 bg-slate-50/70">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
      >
        <div className="max-w-xl">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
            Articles & Case Studies
          </span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
            Our latest posts
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-500">
            Explore the latest insights, trends, and updates in Generative AI and digital transformation.
          </p>
        </div>
        <a
          href="/blog"
          data-testid="blog-all-articles-button"
          className="group inline-flex shrink-0 items-center gap-2.5 rounded-lg border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 hover:border-miti-blue hover:text-miti-blue transition-colors"
        >
          All Articles
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-5">
        {blogPosts.map((post, i) => (
          <motion.a
            key={post.slug}
            href={`/blog/${post.slug}`}
            data-testid={`blog-card-${i + 1}`}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-miti-blue/40 hover:shadow-xl hover:shadow-miti-blue/5 transition-all duration-300"
          >
            <div className="relative h-52 overflow-hidden bg-miti-navy">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute top-4 left-4 rounded-lg bg-white/95 backdrop-blur px-3 py-2 text-center shadow">
                <span className="block font-heading font-bold text-lg leading-none text-slate-900">
                  {formatDay(post.date)}
                </span>
                <span className="block text-[10px] font-semibold uppercase tracking-widest text-miti-blue">
                  {formatMonth(post.date)}
                </span>
              </div>
            </div>
            <div className="p-7">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-miti-blue">{post.category}</span>
              <h3 className="mt-3 font-heading font-semibold text-lg text-slate-900 leading-snug group-hover:text-miti-blue transition-colors">
                {post.title}
              </h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">{post.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-miti-blue">
                Learn more
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </div>
          </motion.a>
        ))}
      </div>
    </div>
  </section>
);

export default Blog;
